import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  FC,
  ReactNode,
} from "react";

export const STEP_IDS = {
  DESTINATION: 1,
  DEGREE: 2,
  FIELD_OF_STUDY: 3,
  CITY: 4,
  NEAREST_OFFICE: 5,
  COACHING: 6,
} as const;

const ALL_STEPS = [
  STEP_IDS.DESTINATION,
  STEP_IDS.DEGREE,
  STEP_IDS.FIELD_OF_STUDY,
  STEP_IDS.CITY,
  STEP_IDS.NEAREST_OFFICE,
  STEP_IDS.COACHING,
];

export interface OnboardingData {
  countries: string[];
  degreeLevel: string;
  studyAreas: string[];
  city: string;
  nearestOffice: string;
  needLoan: string;
  coachingType: string;
  franchiseeUuid: string;
  counsellorId: string;
}

export interface WizardConfig {
  enableB2cNearestOffice?: boolean;
  enableB2cCoaching?: boolean;
  mode?: "page" | "modal";
}

interface IOnboardingContext {
  isOpen: boolean;
  currentStep: number;
  totalSteps: number;
  activeStepId: number;
  data: OnboardingData;
  mode: "page" | "modal";
  enableB2cNearestOffice: boolean;
  enableB2cCoaching: boolean;
  openWizard: (config?: WizardConfig) => void;
  closeWizard: () => void;
  nextStep: () => void;
  prevStep: () => void;
  updateData: (partial: Partial<OnboardingData>) => void;
}

interface IOnboardingProviderProps {
  children: ReactNode;
}

const defaultData: OnboardingData = {
  countries: [],
  degreeLevel: "",
  studyAreas: [],
  city: "",
  nearestOffice: "",
  needLoan: "",
  coachingType: "",
  franchiseeUuid: "",
  counsellorId: "",
};

const OnboardingContext = createContext<IOnboardingContext | undefined>(
  undefined
);

export const useOnboarding = (): IOnboardingContext => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
};

const OnboardingProvider: FC<IOnboardingProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<OnboardingData>(defaultData);
  const [enableB2cNearestOffice, setEnableB2cNearestOffice] = useState(true);
  const [enableB2cCoaching, setEnableB2cCoaching] = useState(true);
  const [mode, setMode] = useState<"page" | "modal">("page");

  const activeSteps = useMemo(
    () =>
      ALL_STEPS.filter((id) => {
        if (id === STEP_IDS.NEAREST_OFFICE) return enableB2cNearestOffice;
        if (id === STEP_IDS.COACHING) return enableB2cCoaching;
        return true;
      }),
    [enableB2cNearestOffice, enableB2cCoaching]
  );

  const totalSteps = activeSteps.length;

  const activeStepId = activeSteps[currentStep - 1] ?? STEP_IDS.DESTINATION;

  const openWizard = useCallback((config?: WizardConfig) => {
    setEnableB2cNearestOffice(config?.enableB2cNearestOffice ?? true);
    setEnableB2cCoaching(config?.enableB2cCoaching ?? true);
    setMode(config?.mode ?? "page");
    setIsOpen(true);
    setCurrentStep(1);
    setData(defaultData);
  }, []);

  const closeWizard = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(1);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const updateData = useCallback((partial: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...partial }));
  }, []);

  const contextValue = useMemo(
    () => ({
      isOpen,
      currentStep,
      totalSteps,
      activeStepId,
      data,
      mode,
      enableB2cNearestOffice,
      enableB2cCoaching,
      openWizard,
      closeWizard,
      nextStep,
      prevStep,
      updateData,
    }),
    [isOpen, currentStep, totalSteps, activeStepId, data, mode, enableB2cNearestOffice, enableB2cCoaching, openWizard, closeWizard, nextStep, prevStep, updateData]
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
